import { useEffect, useState } from 'react';

import { faPencil, faTimes } from '@fortawesome/pro-light-svg-icons';
import { useSelector } from 'hooks';
import { sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Qualification } from 'components/types';

import { formatByLocale } from 'utils/date';

import {
  addEmployeeQualification,
  EmployeeQualification,
  getEmployeeQualifications,
  removeEmployeeQualification,
  updateEmployeeQualification
} from 'RestAPIs/employees/qualifications';
import { getQualifications } from 'RestAPIs/qualifications';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { FontAwesomeSvgIcon } from 'components/shared';

import QualificationDialog, { Props } from './QualificationDialog';

const EmployeeQualificationsStyles = styled.div`
  .add-new-btn {
    margin-bottom: 20px;
  }

  h3 {
    margin-top: 0;
  }
`;

interface EmployeeQualificationsProps {
  employeeId: number;
  dialogContainer: HTMLElement;
}

export default function EmployeeQualifications({
  employeeId,
  dialogContainer
}: EmployeeQualificationsProps) {
  const { t } = useTranslation('common');
  const { hasEmployeeCardEditPreferences } = useSelector(
    (state) => state.grants
  );
  const [showDialog, setShowDialog] = useState(false);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [employeeQualifications, setEmployeeQualifications] = useState<
    EmployeeQualification[]
  >([]);
  const [qualificationToEdit, setQualificationToEdit] =
    useState<EmployeeQualification | null>(null);

  const saveQualification: Props['onSave'] = ({
    qualificationId,
    startDate,
    endDate
  }) => {
    if (qualificationToEdit) {
      updateEmployeeQualification(employeeId, qualificationToEdit.id, {
        qualificationId: qualificationToEdit.qualificationId,
        startDate,
        endDate
      }).then(() => {
        const updatedQualification = {
          ...qualificationToEdit,
          startDate,
          endDate
        };
        const indexOfQualification =
          employeeQualifications.indexOf(qualificationToEdit);
        setEmployeeQualifications(
          sortBy(
            [
              ...employeeQualifications.slice(0, indexOfQualification),
              ...employeeQualifications.slice(indexOfQualification + 1),
              updatedQualification
            ],
            ['qualificationName', 'startDate', 'endDate']
          )
        );
      });
    } else {
      const qualification = qualifications.filter(
        ({ id }) => id === qualificationId
      )[0];
      addEmployeeQualification(employeeId, {
        qualificationId: qualification.id,
        startDate,
        endDate
      }).then(({ data }) => {
        setEmployeeQualifications(
          sortBy(
            [...employeeQualifications, data],
            ['qualificationName', 'startDate', 'endDate']
          )
        );
      });
    }
  };

  const editQualification = (qualification: EmployeeQualification) => {
    setQualificationToEdit(qualification);
    setShowDialog(true);
  };
  const removeQualification = (qualification: EmployeeQualification) => {
    removeEmployeeQualification(employeeId, qualification.id).then(() => {
      const indexOfQualification =
        employeeQualifications.indexOf(qualification);
      setEmployeeQualifications(
        sortBy(
          [
            ...employeeQualifications.slice(0, indexOfQualification),
            ...employeeQualifications.slice(indexOfQualification + 1)
          ],
          ['qualificationName', 'startDate', 'endDate']
        )
      );
    });
  };

  const onDialogHidden = () => {
    setShowDialog(false);
    if (qualificationToEdit) {
      setQualificationToEdit(null);
    }
  };

  useEffect(() => {
    getQualifications().then(({ data }) => {
      setQualifications(
        sortBy(
          data.filter((q) => q.canBeEdited),
          'name'
        )
      );
    });
  }, []);

  useEffect(() => {
    if (employeeId) {
      getEmployeeQualifications(employeeId).then(({ data }) => {
        setEmployeeQualifications(
          sortBy(data, ['qualificationName', 'startDate', 'endDate'])
        );
      });
    } else {
      setEmployeeQualifications([]);
    }
  }, [employeeId]);

  return (
    <EmployeeQualificationsStyles>
      {hasEmployeeCardEditPreferences && (
        <Button
          onClick={() => setShowDialog(true)}
          color="primary"
          variant="contained"
          className="add-new-btn"
        >
          {t('addQualification')}
        </Button>
      )}
      {employeeQualifications.length === 0 && (
        <h3>{t('employeeQualifications.noQualificationsAdded')}</h3>
      )}
      {employeeQualifications.length > 0 && (
        <TableContainer>
          <Table sx={{ width: 650 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('description')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('validFrom')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('validTo')}</strong>
                </TableCell>
                {hasEmployeeCardEditPreferences && (
                  <TableCell align="right">
                    <strong>{t('actions')}</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeQualifications.map((qualification) => (
                <TableRow
                  key={qualification.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell scope="row">
                    {qualification.qualificationName}
                  </TableCell>
                  <TableCell>
                    {formatByLocale(new Date(qualification.startDate), 'PP')}
                  </TableCell>
                  <TableCell>
                    {qualification.endDate
                      ? formatByLocale(new Date(qualification.endDate), 'PP')
                      : t('indefinite')}
                  </TableCell>
                  {hasEmployeeCardEditPreferences && (
                    <TableCell align="right">
                      {qualification.canBeEdited && (
                        <>
                          <IconButton
                            onClick={() => editQualification(qualification)}
                            color="primary"
                            size="small"
                          >
                            <FontAwesomeSvgIcon
                              icon={faPencil}
                              fontSize="small"
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => removeQualification(qualification)}
                            color="primary"
                            size="small"
                          >
                            <FontAwesomeSvgIcon
                              icon={faTimes}
                              fontSize="small"
                            />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {showDialog && (
        <QualificationDialog
          onDialogHidden={onDialogHidden}
          onSave={saveQualification}
          qualifications={qualifications}
          qualificationToEdit={qualificationToEdit}
          dialogContainer={dialogContainer}
        />
      )}
    </EmployeeQualificationsStyles>
  );
}
